import { Component, Input, HostBinding, Output, EventEmitter, OnInit, ElementRef, ViewChild, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import { Option } from '../../models/option.model';

@Component({
  host: {
    '[attr.tabindex]': '0',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TypeaheadComponent), // eslint-disable-line @typescript-eslint/no-use-before-define
    multi: true,
  }],
  selector: 'app-typeahead',
  styleUrls: ['./typeahead.component.scss'],
  templateUrl: './typeahead.component.html',
})
export class TypeaheadComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() value: string | string[]
  @Output() valueChange = new EventEmitter<string | string[]>()

  @Input() @HostBinding('class.disabled') disabled = false
  @Input() options: Option[]
  @Input() placeholder = ''
  @Input() required = false
  @Input() multiselect = false
  @Input() showLabelAndValue = false
  @Input() validators: ValidatorFn[] = []

  @ViewChild('typeaheadInputElement', { static: false }) typeaheadInputElement: ElementRef<HTMLInputElement>
  @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger

  onChange: Function = () => {} // eslint-disable-line @typescript-eslint/no-empty-function
  onTouched: Function = () => {} // eslint-disable-line @typescript-eslint/no-empty-function
  typeaheadInput = new FormControl()
  typeaheadOptions$: Observable<Option[]>

  valueControl = new FormControl()
  errors: ValidationErrors = {}

  ngOnInit(): void {
    this.value = this.value || (this.multiselect ? [] : '');
    this.valueControl = new FormControl(this.value, this.validators);
    this.updateTypeaheadOptions();
    this.valueChange.subscribe(value => {
      this.onChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.updateTypeaheadOptions();
    }
    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }
    if (changes.validators) {
      this.valueControl.setValidators(Validators.compose(this.validators));
    }
  }

  get hasValue(): boolean {
    return (this.value || '').length > 0;
  }

  get hasOverFlow(): boolean {
    return !this.expanded && this.value.length > 1;
  }

  get expanded(): boolean {
    return Array.isArray(this.value) && this.value.length > 0 && this.trigger?.panelOpen;
  }

  get renderedValue(): string | string[] {
    if (!this.expanded && Array.isArray(this.value)) {
      return this.value.slice(0, 1);
    }
    return this.value;
  }

  getDisplayText(value: string): string {
    if (this.options === null) {
      return '';
    }
    const option = this.options.find(option => option.value === value);
    const label = option?.label || value;
    return this.showLabelAndValue ? `${label} - ${value}` : label;
  }

  selectOption(event: MatAutocompleteSelectedEvent): void {
    const { option } = event;
    this.value = this.multiselect ? [...(this.value as string[]), option.value] : option.value;
    this.errorValidation();
    this.valueChange.next(this.value);
    this.typeaheadInput.setValue('');
    this.typeaheadInputElement.nativeElement.value = '';
  }

  clearValue(event?: Event): void {
    this.value = this.multiselect ? [] : '';
    this.errorValidation();
    this.valueChange.next(this.value);
    // prevent "outside" click events on bootstrap dropdown
    if (event) {
      event.stopPropagation();
    }
  }

  errorValidation(): void {
    // validate against this.value, then copy the validation to the typeahead input
    this.valueControl.setValue(this.value);
    this.errors = this.valueControl.errors || {};
    if (Object.keys(this.errors).length > 0) {
      this.typeaheadInput.setValidators(this.validators);
      this.typeaheadInput.markAsTouched();
    } else {
      this.typeaheadInput.setValidators(null);
    }
    if (this.valueControl.errors) {
      this.typeaheadInput.setErrors(this.valueControl.errors);
    }
  }

  clearSearch(event: Event): void {
    // mirror touched status to hidden validated control
    if (this.typeaheadInput.touched && this.valueControl.untouched) {
      this.valueControl.markAsTouched();
    }
    // If we've gotten here by selecting a typeahead option, return
    if (Array.from(get(event, 'target.classList', [])).includes('mat-form-field-autofill-control')) {
      this.errorValidation();
      return;
    }
    this.typeaheadInput.setValue('');
    this.typeaheadInputElement.nativeElement.value = '';
    this.errorValidation();
  }

  removeChip(index: number): void {
    this.value = [
      ...(this.value as string[]).slice(0, index),
      ...(this.value as string[]).slice(index + 1),
    ];
    this.valueChange.next(this.value);
  }

  updateTypeaheadOptions(): void {
    this.typeaheadOptions$ = this.typeaheadInput.valueChanges.pipe(
      startWith(''),
      filter(value => typeof value === 'string'),
      map(value => {
        const searchString = value.toLowerCase();
        return searchString.length > 0
          ? this.options.filter(({ label, value }) => (`${label} ${value}`).toLowerCase().includes(searchString))
          : this.options;
      }),
    );
  }

  openTypeahead(event: Event): void {
    event.stopPropagation();
    if (!this.trigger?.panelOpen) {
      this.trigger.openPanel();
      setTimeout(() => this.typeaheadInputElement.nativeElement.focus());
    }
  }

  writeValue(value: string | string[]): void {
    this.value = value;
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
    this.typeaheadInput[this.disabled ? 'disable' : 'enable']();
  }
}
